import React, { useEffect } from 'react';
import { Contact, Phone } from '../../reducers/ContactReducer';
import { isMobileDevice } from '../../utilities/is_mobile';
import { normalizePhone } from '../../utilities/formatting/data_normalizations';
import { Strings } from '../../assets/common/strings';
import { connect } from '@optum-uhone-hmkts/rise';
import { fullName } from '../../assets/common/string_builders';
import { SelectDialog, ListSelectionAction } from './templates/select_dialog';
import jwt_auth from '../../utilities/auth/jwt_auth';
import { P } from '../../utilities/auth/permissions';
import { orderContacts } from '../../utilities/contact_util';
import { isPhoneWithinGracePeriod } from '../../utilities/phone_email_util';
import { ContactVerificationResult } from '../../reducers/click_to_call_reducer';
import { 
	ClearDncStatus,
	CreateContactAttempt,
	CreateContactAttemptParams,
	DncQueryParams,
	GetDncStatus
} from '../../actions/compliance_actions';
import { Activity } from '../../reducers/activity_reducer';
import { ComplianceRecord } from '../../reducers/compliance_reducer';

type PartialSelectDialogProps = {
	title: string;
	listTitle: string;
	secondaryListTitle: string;
	selectActionIconClass: string;
	selectActionLabel: string;
};

type CommunicationListItemProps = {
	primary: string;
	secondary: string;
	isDisabled?: boolean;
	id: string;
	value: string;
	isLoading?: boolean;
}

type StateProps = {
	desktopCallEnabled: boolean;
	desktopTextEnabled: boolean;
	leadListMultiSelect: boolean;
	templateTitle: string;
	click2callEnabled: boolean;
	verificationResult?: ContactVerificationResult;	
	agentId: string;
	complianceByPhone: ComplianceRecord;
}
interface OwnProps {
	open: boolean;
	handleClose: () => void;
	handleSelection: (selection: { id: string, value: string }[]) => void;
	selectableCount?: number;
	activityType: Strings.Activity;
	contacts: Contact[];
	secondaryActions?: ListSelectionAction[];
	autoselect?: boolean;
	hasActiveBusinessRelationship?: boolean;
	activities?: Activity[];
}

interface DispatchProps {
	fetchDncStatus: (params: DncQueryParams) => void;
	createContactAttempt: (params: CreateContactAttemptParams) => void;
	clearDncStatus: () => void;
}

type Props = StateProps & OwnProps & DispatchProps;

const SelectContactDialogInternal: React.FC<Props> = props => {
	const {
		desktopCallEnabled,
		open,
		contacts,
		handleClose,
		handleSelection,
		selectableCount,
		activityType,
		secondaryActions,
		leadListMultiSelect,
		templateTitle,
		hasActiveBusinessRelationship,
		click2callEnabled,
		verificationResult,
		activities,
		complianceByPhone,
	} = props;
	
	const hasDispatchedRef = React.useRef(false);

	useEffect(() => {		
		if (
			!open ||
			!contacts?.length ||
			activityType !== Strings.Activity.PhoneCall
		) {
			return;
		}

		if (hasDispatchedRef.current) return;

		hasDispatchedRef.current = true;
		
		const hasOpenAppointment = (activities || []).length > 0;

		contacts.forEach(contact => {
			contact.phones.forEach(phone => {				
				props.fetchDncStatus({
					phone: phone.number,
					hasCallback: hasActiveBusinessRelationship || hasOpenAppointment,
					agentCode: props.agentId,
					zipCode: contact.addresses?.[0]?.zipCode
				});
			});
		});
	}, [
		open,
		contacts
	]);
	
	useEffect(() => {
		if (!open) return;

		return () => {
			props.clearDncStatus();
			hasDispatchedRef.current = false;
		}
	}, [open]);

	const getSelectDialogProps = (): PartialSelectDialogProps => {

		let result: PartialSelectDialogProps = {
			title: '',
			listTitle: Strings.SelectContact.PreferredNumbers,
			secondaryListTitle: Strings.SelectContact.OtherNumbers,
			selectActionIconClass: '',
			selectActionLabel: ''
		}

		switch (activityType) {
			case Strings.Activity.PhoneCall:
				result.title = desktopCallEnabled || isMobileDevice ? 'Make a call to:' : 'Made call to:';
				result.selectActionIconClass = 'icon-call-check-bttn';
				result.selectActionLabel = 'Next';
				break;

			case Strings.Activity.Sms:
				result.title = 'Send SMS to:';
				if (jwt_auth.hasPermission(P.SmsTemplateCreate) && !leadListMultiSelect) {
					result.selectActionIconClass = 'icon-text-contact-button';
					result.selectActionLabel = 'USE TEMPLATE';
				}
				break;

			case Strings.Activity.Email:
				result = {
					title: 'Send email to:',
					listTitle: Strings.SelectContact.PreferredEmails,
					secondaryListTitle: Strings.SelectContact.SecondaryEmails,
					selectActionIconClass: 'icon-email-contact-button',
					selectActionLabel: 'Open Email'
				};
				break;

			case Strings.Activity.ClientConnect:
				result.title = 'Share ClientConnect to:';
				result.listTitle = isMobileDevice ? Strings.SelectContact.PreferredNumbers : Strings.SelectContact.PreferredEmails;
				result.secondaryListTitle = isMobileDevice ? Strings.SelectContact.OtherNumbers : Strings.SelectContact.SecondaryEmails;
				result.selectActionIconClass = isMobileDevice ? 'icon-text-contact-button' : 'icon-email-contact-button';
				result.selectActionLabel = 'Share';
				break;
		}

		return result;
	}

	const getListItems = (contacts: Contact[], activityType: Strings.Activity): { listItems: CommunicationListItemProps[], secondaryListItems: CommunicationListItemProps[] } => {
		switch (activityType) {

			case (Strings.Activity.Email):
				return getEmailListItems(contacts);

			case (Strings.Activity.PhoneCall):
				return getPhoneListItems(contacts, undefined);

			case (Strings.Activity.Sms):
				return getPhoneListItems(contacts, undefined);

			case (Strings.Activity.ClientConnect):
				return isMobileDevice
					? getPhoneListItems(contacts, Strings.PhoneTypes.Mobile)
					: getEmailListItems(contacts);

			default:
				return { listItems: [], secondaryListItems: [] }
		}
	}

	const getEmailListItems = (contacts: Contact[]) => {
		const listItems: CommunicationListItemProps[] = [];
		const preferredListItems: CommunicationListItemProps[] = [];

		contacts.forEach(contact => {
			contact.emails.forEach(email => {
				const item: CommunicationListItemProps = {
					primary: fullName(contact),
					secondary: email.type + ': ' + email.emailAddress,
					value: email.emailAddress,
					id: contact.id,
					isDisabled: contact.status == Strings.Status.Inactive || email.doNotEmail
				};

				if (email.isPreferred)
					preferredListItems.push(item);
				else
					listItems.push(item);
			});
		});
		return { listItems: preferredListItems, secondaryListItems: listItems };
	}

	const handlePhoneSelection = (selection: { id: string, value: string }[]) => {
		if (!selection.length) return;
		
		if (activityType !== Strings.Activity.PhoneCall) {
			handleSelection(selection);
			return;
		}

		// Create contact attempt
		const selected = selection[0];
		const contact = contacts.find(c => c.id === selected.id);

		const zipCode = contact?.addresses?.[0]?.zipCode;

		const hasOpenAppointment = (activities || []).length > 0;

		props.createContactAttempt({
			phone: selected.value,
			hasCallback: hasActiveBusinessRelationship || hasOpenAppointment,
			zipCode: zipCode
		});
		handleSelection(selection);
	}

	const getPhoneListItems = (contacts: Contact[], phoneType?: Strings.PhoneTypes) => {
		const listItems: CommunicationListItemProps[] = [];
		const preferredListItems: CommunicationListItemProps[] = [];

		if (contacts) {
			const ordered = orderContacts(contacts);
			ordered.forEach(contact => {
				contact.phones.forEach(phone => {
					if (!phoneType || phone.type == phoneType) {
						const isLoading = complianceByPhone?.[phone.number]?.loading;
						const isDisabled = isLoading || isDncPhone(contact, phone);
						const reason = isDisabled ? getPhoneDisableReason(phone) : undefined;
						const secondaryText = `${phone.type}: ${normalizePhone(phone.number)}
							${reason ? `(DNC Rule: ${reason})` : ''}`;

						const item: CommunicationListItemProps = {
							primary: fullName(contact),
							secondary: secondaryText,								
							isDisabled: isDisabled,
							value: phone.number,
							id: contact.id,
							isLoading,
						};

						if (phone.isPreferred) {
							if (!preferredListItems.find(i => i.value == item.value)) {
								preferredListItems.push(item);
							}
						} else {
							if (!listItems.find(i => i.value == item.value)) {
								listItems.push(item);
							}
						}
					}
				});
			});
		}
		return { listItems: preferredListItems, secondaryListItems: listItems };
	}

	function isDncPhone(contact: Contact, phone: Phone): boolean {
		// For phone calls, check dnc status from compliance
		if (activityType === Strings.Activity.PhoneCall) {			
			const compliance = complianceByPhone?.[phone.number];
			if (!compliance)
				return true;

			return compliance.isDnc ?? true;
		}

		// Use existing dnc statuses from its internal properties
		const isSms = activityType === Strings.Activity.Sms;

		return Boolean(
			phone.isCompanyDnc ||
			(
				phone.isNationalDnc &&
				!isPhoneWithinGracePeriod(phone) &&
				!hasActiveBusinessRelationship
			) ||
			(isSms && phone.isSmsDnc) ||
			contact.status === Strings.Status.Inactive ||
			(
				click2callEnabled &&
				verificationResult?.sessionHousehold?.households?.some(
					result =>
						contact.householdId === result.householdId &&
						result.wasCalled
				)
			)
		);
	}
	
	function getPhoneDisableReason(phone: Phone): string | undefined {
		if (activityType !== Strings.Activity.PhoneCall)
			return undefined;

		const compliance = complianceByPhone?.[phone.number];

		if (!compliance) return undefined;
		if (compliance?.isDnc) 
				return compliance.reason || 'Do Not Call';
		
		return undefined;
	}

	const allListItems = getListItems(contacts, activityType);
	const { listItems, secondaryListItems } = allListItems;
	const options = [...listItems, ...secondaryListItems];

	return <SelectDialog
		key="select-contact-dialog"
		open={open}
		alternateTitle={leadListMultiSelect ? templateTitle : ''}
		selectableCount={leadListMultiSelect ? options.length : selectableCount || 1}
		handleClose={handleClose}
		handleSelect={handlePhoneSelection}
		secondaryActions={secondaryActions}
		{...getSelectDialogProps()}
		{...allListItems}
	/>
}

function mapStateToProps(state): StateProps {

	const { template } = state.smsSequence;
	return {
		desktopTextEnabled: state.user.enableDesktopTexts,
		desktopCallEnabled: state.user.enableDesktopCalls,
		agentId: state.user.agentID,
		leadListMultiSelect: state.smsSequence.leadListMultiSelect,
		templateTitle: template ? template.subject : '',
		click2callEnabled: state.clickToCall.enabled,
		verificationResult: state.clickToCall.verificationResult,
		complianceByPhone: state.compliance?.byPhone ?? {},
	};
}

function mapDispatchToProps(dispatch): DispatchProps {
	return {
		fetchDncStatus: params => {
			dispatch(GetDncStatus.started(params))
		},
		createContactAttempt: params => {
			dispatch(CreateContactAttempt.started(params))
		},
		clearDncStatus: () => {
			dispatch(ClearDncStatus());
		}
	};
}

export const SelectContactDialog = connect<StateProps, DispatchProps, OwnProps>(
	mapStateToProps, 
	mapDispatchToProps, 
	true)(SelectContactDialogInternal);